import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getServerUrl } from '../config/runtimeConfig';

const NetworkTest = () => {
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const backendUrl = getServerUrl();
  const frontendOrigin = typeof window !== 'undefined' ? window.location.origin : 'unknown';

  const addResult = (message, success = true, details = null) => {
    setResults(prev => [...prev, { 
      message, 
      success, 
      timestamp: new Date().toISOString(),
      details: details ? JSON.stringify(details, null, 2) : null
    }]);
  };

  const runTests = async () => {
    setLoading(true);
    setError(null);
    setResults([]);

    // Test 1: OPTIONS preflight request
    try {
      addResult('Testing OPTIONS preflight request...');
      const preflightResponse = await fetch(`${backendUrl}/ping`, {
        method: 'OPTIONS',
        headers: {
          'Origin': frontendOrigin,
          'Access-Control-Request-Method': 'GET',
          'Access-Control-Request-Headers': 'content-type, accept'
        },
        mode: 'cors',
        credentials: 'include'
      });
      
      addResult('Preflight request successful', true, {
        status: preflightResponse.status,
        headers: Object.fromEntries([...preflightResponse.headers])
      });
    } catch (err) {
      addResult(`Preflight request failed: ${err.message}`, false, {
        error: err.toString(),
        stack: err.stack
      });
    }

    // Test 2: Direct fetch
    try {
      addResult('Starting direct fetch test...');
      const response = await fetch(`${backendUrl}/ping`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        mode: 'cors',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const text = await response.text();
      addResult('Direct fetch successful', true, {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries([...response.headers]),
        body: text
      });
    } catch (err) {
      addResult(`Direct fetch failed: ${err.message}`, false, {
        error: err.toString(),
        stack: err.stack
      });
    }

    // Test 3: Axios with minimal headers
    try {
      addResult('Starting Axios test...');
      const axiosConfig = {
        method: 'get',
        url: `${backendUrl}/ping`,
        headers: {
          'Accept': 'application/json'
        },
        withCredentials: true
      };

      const response = await axios(axiosConfig);
      
      addResult('Axios request successful', true, {
        config: {
          url: response.config.url,
          method: response.config.method,
          headers: response.config.headers
        },
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        data: response.data
      });
    } catch (err) {
      let errorDetails = {
        message: err.message,
        name: err.name
      };

      if (err.response) {
        errorDetails.response = {
          status: err.response.status,
          statusText: err.response.statusText,
          headers: err.response.headers,
          data: err.response.data
        };
      }

      if (err.config) {
        errorDetails.config = {
          url: err.config.url,
          method: err.config.method,
          headers: err.config.headers
        };
      }

      addResult(`Axios request failed: ${err.message}`, false, errorDetails);
    }

    // Test 4: CORS test endpoint
    try {
      addResult('Testing CORS test endpoint...');
      const response = await fetch(`${backendUrl}/cors-test`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        mode: 'cors',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      addResult('CORS test endpoint successful', true, {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries([...response.headers]),
        data
      });
    } catch (err) {
      addResult(`CORS test endpoint failed: ${err.message}`, false, {
        error: err.toString(),
        stack: err.stack
      });
    }

    setLoading(false);
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h2>Network Connectivity Test</h2>
      <button 
        onClick={runTests} 
        disabled={loading}
        style={{ 
          padding: '10px 20px', 
          fontSize: '16px', 
          marginBottom: '20px',
          backgroundColor: loading ? '#cccccc' : '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: loading ? 'not-allowed' : 'pointer'
        }}
      >
        {loading ? 'Running Tests...' : 'Run Network Tests'}
      </button>

      <div style={{ 
        border: '1px solid #ddd', 
        borderRadius: '4px',
        padding: '15px',
        backgroundColor: '#f8f9fa'
      }}>
        <h3>Test Results:</h3>
        {results.map((result, index) => (
          <div 
            key={index}
            style={{
              padding: '10px',
              margin: '5px 0',
              backgroundColor: result.success ? '#d4edda' : '#f8d7da',
              border: `1px solid ${result.success ? '#c3e6cb' : '#f5c6cb'}`,
              borderRadius: '4px',
              color: result.success ? '#155724' : '#721c24'
            }}
          >
            <div>
              <span style={{ color: '#666', fontSize: '0.8em' }}>
                {new Date(result.timestamp).toLocaleTimeString()} -{' '}
              </span>
              {result.message}
            </div>
            {result.details && (
              <pre style={{ 
                marginTop: '10px',
                padding: '10px',
                backgroundColor: 'rgba(0,0,0,0.05)',
                borderRadius: '3px',
                fontSize: '0.85em',
                overflowX: 'auto'
              }}>
                {result.details}
              </pre>
            )}
          </div>
        ))}
        {results.length === 0 && (
          <p style={{ color: '#666', fontStyle: 'italic' }}>
            No tests run yet. Click the button above to start testing.
          </p>
        )}
      </div>
    </div>
  );
};

export default NetworkTest;
